import { MutableRefObject, useState, useEffect } from "react";
import {
  SecurityTypeCode,
  SecurityTradeType,
  SecuritySubTypeCode,
} from "api/holdings/types";
import { useGetSecurityDetails } from "api/holdings/useGetSecurityDetails";
import { useGetContactInfo } from "api/initial/useGetContactInfo";
import { useGetBuyData, BankAccount } from "api/trading/useGetBuyData";
import { ExecutionMethod, useTrade } from "api/trading/useTrade";
import {
  PortfolioSelect,
  DownloadableDocument,
  Button,
  Input,
  LabeledDiv,
  Select,
} from "components/index";
import { Option } from "components/Select/Select";
import {
  APPROVED_PORTFOLIO,
  ASK_APPROVED_FUNDS,
  ASK_PORTFOLIO_CODE,
  BANK_APPROVED,
  INSTALLATION_CURRENCY,
} from "config";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { useGetContactIdData } from "providers/ContactIdProvider";
import { useKeycloak } from "providers/KeycloakProvider";
import { addProtocolToUrl } from "utils/url";
import { useTradablePortfolioSelect } from "../useTradablePortfolioSelect";

export interface BankAccountOption extends Option {
  value: BankAccount;
}

export interface BuyModalInitialData {
  id: number;
}

interface BuyModalProps extends BuyModalInitialData {
  modalInitialFocusRef: MutableRefObject<null>;
  onClose: () => void;
}

const isSecurityTypeFund = (securityType: SecurityTypeCode | undefined) =>
  securityType === SecurityTypeCode.COLLECTIVE_INVESTMENT_VEHICLE || securityType === SecurityTypeCode.FUND;


const getTradeType = (securityType: SecurityTypeCode | undefined) =>
  isSecurityTypeFund(securityType) ? "subscription" : "buy";

const getTradeAmount = (
  amount: number,
  isTradeInUnits: boolean | undefined,
  price = 1,
  fxRate = 1
) => (isTradeInUnits ? amount * price * fxRate : amount);

const getTradeAmountArgs = (
  amount: number,
  isTradeInUnits: boolean | undefined
) => (isTradeInUnits ? { units: amount } : { tradeAmount: amount });

export const BuyModalContent = ({
  modalInitialFocusRef,
  onClose,
  id: securityId,
}: BuyModalProps) => {
  const {
    data: security = {
      name: "",
      url2: undefined,
      type: { code: undefined },
      latestMarketData: undefined,
      fxRate: 1,
      securityCode: "",
      isinCode: "",
      currency: { securityCode: "" },
      tagsAsSet: [],
      minTradeAmount: 1,
      figuresAsObject: { latestValues: { KIID: { value: "" } } },
      securitySubType: { code: SecuritySubTypeCode },
    },
  } = useGetSecurityDetails(securityId.toString());
  const {
    name: securityName,
    url2 = undefined,
    type: { code: securityType } = {},
    latestMarketData,
    fxRate,
    tagsAsSet: securityTags,
  } = security;
  const [termsAndConditionCheck, setTermsAndConditionCheck] = useState(false);
  const handleConfirm = async () => {
    const response = await handleBuy();
    if (response) {
      onClose();
    }
  };
  const handleCheckboxChange = () => {
    setTermsAndConditionCheck((oldState) => !oldState);
  };
  const kiidDocument =
    security.figuresAsObject.latestValues.KIID?.value || undefined;

  const [isTradeInUnits, setIsTradeInUnits] = useState(true);
  const unitPrice = isTradeInUnits ? "AUTO" : "1";

  const [canToggleTradeType, setCanToggleTradeType] = useState(false);
  //in case it takes some time to loop through tags and deduct trade type
  const [hasDeductedTradeType, setHasDeductedTradeType] = useState(false);
  useEffect(() => {
    setHasDeductedTradeType(false);
    const isTradeTypeSpecified = securityTags?.some(
      (tag) =>
        tag === SecurityTradeType.buyUnits ||
        tag === SecurityTradeType.buyTradeAmount
    );
    const isUnitsSupported = securityTags?.some(
      (tag) => tag === SecurityTradeType.buyUnits
    );
    const isTradeAmountSupported = securityTags?.some(
      (tag) => tag === SecurityTradeType.buyTradeAmount
    );
    const isUnitsDefaultTradeType = !isSecurityTypeFund(securityType);
    setCanToggleTradeType(
      isTradeTypeSpecified && isUnitsSupported && isTradeAmountSupported
    );
    setIsTradeInUnits(
      isTradeTypeSpecified
        ? isUnitsSupported &&
            (!isTradeAmountSupported || isUnitsDefaultTradeType)
        : isUnitsDefaultTradeType
    );
    setHasDeductedTradeType(true);
  }, [securityTags, securityType]);

  const { t } = useModifiedTranslation();
  const { selectedContactId } = useGetContactIdData();
  const {
    data: { portfolios, contactHoldings } = {
      portfolios: [],
      contactHoldings: [],
    },
  } = useGetContactInfo(false, selectedContactId);

  const currentHolding =
    contactHoldings?.find(
      (holding) => holding.security.isinCode === security.isinCode
    )?.marketValue || 0;

  const { setPortfolioId, portfolioOptions, portfolioId } =
    useTradablePortfolioSelect();

  const selectedPortfolioId = portfolioId;

  const portfolioType = portfolios?.find(
    (obj) => obj.id === selectedPortfolioId
  )?.type.code;

  const portfolioTags: string[] =
    portfolios?.find((obj) => obj.id === selectedPortfolioId)?.tagsAsSet || [];

  const portfolioApprovedKAR = portfolioTags.includes(APPROVED_PORTFOLIO);

  const canTradeNonEquity = portfolioType === ASK_PORTFOLIO_CODE ? false : true;

  const canTradeASKFund =
    ASK_APPROVED_FUNDS.includes(String(security.securitySubType?.code)) ||
    canTradeNonEquity;

  const {
    loading,
    data: {
      availableCash = 0,
      currency: portfolioCurrency = INSTALLATION_CURRENCY,
      bankAccounts: accountList = [],
      accountBalances: availableBalancesList = [],
      bankMerged: mergedBankList = [],
    } = {},
  } = useGetBuyData(selectedPortfolioId);

  const getBankAccounts = (allAccounts: BankAccount[] = mergedBankList) => {
    if (allAccounts.length === 0) {
      return [];
    } else {
      return allAccounts
        .filter(
          (e) =>
            e.cashAccount === true ||
            e.tags.toLowerCase().includes(BANK_APPROVED.toLowerCase())
        )
        .map((bank) => ({
          ...bank,
          checkAccountAmount: bank.cashAccount ? true : false,
        }));
    }
  };

  const bankAccountList = getBankAccounts();

  const bankAccountOptions = bankAccountList.map((account) => {
    return {
      id: account.id,
      value: account,
      label: account.number,
      OptionComponent: <div>{`${account.name} - ${account.number}`}</div>,
    };
  });

  const [selectedCashAccount, setSelectedCashAccount] = useState<
    BankAccountOption | undefined
  >(bankAccountOptions[0]);

  const [amount, setAmount] = useState(0);

  const { handleTrade: handleBuy, submitting } = useTrade({
    tradeType: getTradeType(securityType),
    portfolio:
      portfolios.find((portfolio) => portfolio.id === portfolioId) ||
      portfolios[0],
    securityName,
    ...getTradeAmountArgs(amount, isTradeInUnits),
    ...security,
    currency: security.currency.securityCode,
    executionMethod: isTradeInUnits
      ? ExecutionMethod.UNITS
      : ExecutionMethod.NET_TRADE_AMOUNT,
    unitPrice: unitPrice,
    account: selectedCashAccount?.value.number || "",
  });
  /*
    const [checkAmount, setCheckAmount] = useState(true)
  */

  const checkAmount = !bankAccountList.length
    ? true
    : selectedCashAccount?.value?.cashAccount
    ? true
    : false;

  const expectedTradeAmount = getTradeAmount(
    amount,
    isTradeInUnits,
    latestMarketData?.price,
    fxRate
  );

  const availableCashForChosenAccount = (selectedCashAccount?.value.balanceAccCurr || 0) + (selectedCashAccount?.value.amountFromOpenTradeOrders || 0)

  const isTradeAmountCorrect = !checkAmount
    ? true
    : !isNaN(availableCashForChosenAccount) && amount >= 0 && amount <= availableCashForChosenAccount;

  const isTradeAmountAboveMinimum =
    expectedTradeAmount + currentHolding >= security.minTradeAmount;


  const { readonly } = useKeycloak();

  return (
    <div className="grid gap-2 min-w-[min(84vw,_375px)]">
      <LabeledDiv
        label={t("tradingModal.securityName")}
        className="text-2xl font-semibold"
      >
        {securityName}
      </LabeledDiv>
      <LabeledDiv
        label={t("tradingModal.isinCode")}
        className="text-xl font-semibold"
      >
        {security.isinCode}
      </LabeledDiv>
      {
        // Changed from url2 to kiidDocument
        kiidDocument && (
          <div className="w-fit">
            <DownloadableDocument
              url={addProtocolToUrl(kiidDocument)}
              label={t("tradingModal.kiid")}
            />
          </div>
        )
      }
      {
        // Changed from url2 to kiidDocument
        currentHolding && (
          <div className="w-fit">
            <LabeledDiv
              label={t("tradingModal.currentHoldingTotal")}
              className="text-xl font-semibold"
            >
              {portfolioCurrency &&
                t("numberWithCurrency", {
                  value: currentHolding,
                  currency: portfolioCurrency,
                })}
            </LabeledDiv>
          </div>
        )
      }

      <PortfolioSelect
        portfolioOptions={portfolioOptions}
        portfolioId={portfolioId}
        onChange={(newPortfolio) => {
          setPortfolioId(newPortfolio.id);
          setSelectedCashAccount(bankAccountOptions[0]);
        }}
        label={t("tradingModal.portfolio")}
      />

      {!loading && bankAccountList.length ? (
        <Select
          value={selectedCashAccount}
          options={bankAccountList.map((account) => {
            return {
              id: account.id,
              value: account,
              label: account.number,
              OptionComponent: (
                <div>{`${account.name} - ${account.number}`}</div>
              ),
            };
          })}
          onChange={(option) => setSelectedCashAccount(option)}
          label={t("tradingModal.bankAccount")}
        ></Select>
      ) : portfolioId && !loading ? (
        <div className="max-w-sm">{t("tradingModal.noBankAccounts")}</div>
      ) : (
        ""
      )}

      {portfolioId && !loading && !bankAccountList.length ? (
        <div className="max-w-sm">{t("tradingModal.noBankAccounts")}</div>
      ) : (
        ""
      )}

      {checkAmount && !loading ? (
        <LabeledDiv
          label={t("tradingModal.availableCash")}
          className="text-xl font-semibold text-gray-700"
        >
          {portfolioCurrency &&
            t("numberWithCurrency", {
              value: availableCashForChosenAccount,
              currency: portfolioCurrency,

              defaultValue: 0,
            })}
        </LabeledDiv>
      ) : (
        ""
      )}

      <Input
        disabled={!hasDeductedTradeType || !portfolioId}
        ref={modalInitialFocusRef}
        value={amount || ""}
        onChange={(event) => {
          setAmount(Number(event.currentTarget.value));
        }}
        label={
          isTradeInUnits
            ? t("tradingModal.unitsInputLabel")
            : t("tradingModal.tradeAmountInputLabel", {
                currency: portfolioCurrency,
              })
        }
        type="number"
        error={
          !isTradeAmountCorrect && !loading
            ? t("tradingModal.tradeAmountInputError")
            : amount && !isTradeAmountAboveMinimum && !loading
            ? t("tradingModal.tradeAmountBelowMiniumum", {
                minValue: security.minTradeAmount,
              })
            : !canTradeASKFund && !loading
            ? t("tradingModal.cantTradeAsk")
            : !portfolioApprovedKAR && !loading && portfolioId
            ? t("tradingModal.KARNotApproved")
            : undefined
        }
      />

      {canToggleTradeType && (
        <>
          <div className="flex overflow-hidden font-medium leading-5 bg-gray-50 rounded-md divide-x ring-1 shadow-sm pointer-events-auto select-none divide-slate-400/20 text-[0.8125rem] ring-slate-700/10">
            <button
              className={`text-center cursor-pointer py-2 px-4 flex-1 ${
                isTradeInUnits ? "bg-gray-200" : ""
              }`}
              onClick={() => setIsTradeInUnits(true)}
            >
              {t("tradingModal.unitsButtonLabel")}
            </button>

            <button
              className={`text-center cursor-pointer py-2 px-4 flex-1 ${
                !isTradeInUnits ? "bg-gray-200" : ""
              }`}
              onClick={() => setIsTradeInUnits(false)}
            >
              {t("tradingModal.tradeAmountButtonLabel")}
            </button>
          </div>
        </>
      )}

      <hr className="my-2" />
      <div className="flex flex-col sm:max-w-sm md:max-w-lg text-xs text-left">
        <p className="">{t("tradingModal.disclaimer")}</p>
        <a
          className="mb-2"
          target="_blank"
          href="https://www.alfredberg.no/regler-og-retningslinjer/"
          rel="noreferrer"
        >
          {"https://www.alfredberg.no/regler-og-retningslinjer/"}
        </a>
        <label>
          <input
            className="mr-2"
            type="checkbox"
            checked={termsAndConditionCheck}
            onChange={handleCheckboxChange}
          />
          Jeg har lest og forstått fondets nøkkelinformasjon.
        </label>
      </div>
      <div className="flex flex-col gap-4 items-stretch ">
        <div className="text-3xl font-semibold text-center">
          <div className="text-base font-normal">
            {t("tradingModal.tradeAmount")}
          </div>
          {t("numberWithCurrency", {
            value: isTradeAmountCorrect ? expectedTradeAmount : 0,
            currency: portfolioCurrency,
          })}
        </div>

        <Button
          disabled={
            readonly ||
            amount === 0 ||
            loading ||
            !isTradeAmountCorrect ||
            !isTradeAmountAboveMinimum ||
            !portfolioId ||
            !portfolioApprovedKAR ||
            !selectedCashAccount ||
            !termsAndConditionCheck
          }
          isLoading={submitting}
          onClick={handleConfirm}
        >
          {t("tradingModal.buyButtonLabel")}
        </Button>
      </div>
      <hr className="my-1" />
      <div className="text-xs text-center text-gray-600 max-w-[375px]">
        {t("tradingModal.buyDisclaimer")}
      </div>
    </div>
  );
};
